import { Avatar, AvatarSizes, AvatarShapes } from '@clockout-dev/design-elements';
import { TicketIcon, UserIcon } from '@heroicons/react/24/outline';
import MoneyController from '../../../core/money.controller';
import { MoneyTones } from '../../../constants/money.constants';
import SignedAmount from '../signedAmount/SignedAmount';
import TransactionTypeTag from '../transactionTypeTag/TransactionTypeTag';
import {
    ADVANCE_TYPE_LABELS,
    DESTINATION_SOURCE,
    LEDGER_DESTINATION_UNKNOWN_LABEL,
    LEDGER_PENDING_LABEL,
    LEDGER_PROCESSED_STATUS,
    LEDGER_TABLE_PLACEHOLDER,
    LEDGER_TIMEZONE,
    LEDGER_USER_UNKNOWN_LABEL,
} from '../../../constants/ledger.constants';
import {
    formatLedgerDate,
    formatLedgerTime,
    formatLedgerTimestampTitle,
    formatLedgerTimezoneLabel,
} from '../../../utils/dates';
import { getLedgerSortOrder, getLedgerTypeFilterOptions, isReconciliationRow, resolveFee } from '../../../utils/ledger';
import {
    LEDGER_COLUMN_TITLES,
    LEDGER_EMPTY_CELL_LABEL,
    LEDGER_RECONCILIATION_TAG_LABEL,
    LEDGER_RECONCILIATION_TAG_TITLE,
    LEDGER_ROW_ACTION,
    LEDGER_ROW_ACTION_LABELS,
} from './ledgerTable.constants';
import styles from './styles/ledger-table.module.scss';

/**
 * The ledger table's COLUMNS and cell renderers — nothing else.
 *
 * Every method here returns JSX or picks the words for a cell, and every one is
 * called by `getLedgerColumns`. The rules that are not about rendering are plain
 * functions instead: the query-string contract, the endpoint's params, the fee
 * and the reconciliation flag in `utils/ledger.js`, the timestamp and zone
 * formatting in `utils/dates.js`. A page controller that needs to read the
 * table's state back imports those directly, so it never inherits fifteen cell
 * renderers it does not call — and cannot have its own `formatDate` silently
 * overwritten by one of them, which is what `core/inherit.js` copying own
 * prototype properties would do.
 *
 * Shared by Reports › Transactions and the User page, which differ in exactly
 * two things — whether the User column shows (`showUserColumn`) and what the
 * per-row action does (`rowAction`) — and in nothing else: column order,
 * formatters, tones and empty-state copy live here and in the constants, so
 * the two screens cannot disagree.
 *
 * Extends `MoneyController`, which decides what colour every amount gets: green
 * only for money settled, neutral for descriptive figures and for a
 * reconciliation, red left to the surfaces that can establish a debt. No amount
 * here picks its own colour, and colour is never the only signal — the Type
 * column labels each row in words and tags a reconciliation, so a row reads
 * correctly with colour ignored entirely.
 *
 * Mixed in one way only, as the BASE of the table shell
 * (`Inherit(LedgerTableController, Component)`, the same shape the design system
 * uses for `TableController`), which is the wiring that keeps what it inherits
 * from `MoneyController` intact.
 *
 * The design system's `PaginationController` owns the query string: with
 * `saveSearchParams` the Table writes page, rows-per-page, the sorted column and
 * every filter itself, and `getLedgerStateFromUrl` reads that back. Nothing here
 * writes the URL — one writer, no double-encoding.
 */
class LedgerTableController extends MoneyController {
    /**
     * A timestamp as date over time, stacked.
     *
     * Stacked rather than inline because this table already carries eight
     * columns in a narrow split-layout pane: "09/01/2026 · 14:41:07" on one
     * line would widen two of them by roughly half again. The `title` carries
     * the full timestamp, with its zone and offset, for anyone who needs to
     * copy it.
     *
     * @param {string|Date|null} value
     * @param {string} [placeholder]
     * @param {string} [zone] IANA zone to render in
     */
    renderTimestamp(value, placeholder = LEDGER_TABLE_PLACEHOLDER, zone = LEDGER_TIMEZONE) {
        const date = formatLedgerDate(value, null, zone);
        if (!date) return placeholder;
        const time = formatLedgerTime(value, zone);

        return (
            <span className={styles.timestamp} title={formatLedgerTimestampTitle(value, zone)}>
                <span>{date}</span>
                {time ? <span className={styles.timestampTime}>{time}</span> : null}
            </span>
        );
    }

    /**
     * A settlement carries a transfer too (`transfer_type: 'settle'`), but it is
     * not an advance — only instant/standard belong under this heading.
     * Anything else is not missing, it does not apply, so the cell is empty
     * rather than dashed.
     * @param {string|null} transferType
     */
    renderAdvanceType(transferType) {
        return ADVANCE_TYPE_LABELS[transferType] || this.renderEmptyCell();
    }

    /**
     * A visually empty cell that still has an accessible name. Used where a
     * value does not apply (advance type on a non-advance) — a dash there reads
     * as "missing", and nothing is missing.
     */
    renderEmptyCell() {
        return (
            <span className={styles.emptyCell}>
                <span className={styles.srOnly}>{LEDGER_EMPTY_CELL_LABEL}</span>
            </span>
        );
    }

    /**
     * When the money actually moved.
     *
     * The endpoint has already resolved this: for a settlement or a service fee
     * `processed_at` is the created date, because neither has a money-movement
     * event separate from the row being written. Deriving it here as well would
     * put the same rule in two places and let the column disagree with the sort,
     * which is exactly what happened before — so this only picks the words.
     *
     * @param {string|null} processedAt
     * @param {object} record
     * @param {string} [zone] IANA zone to render in
     */
    renderProcessedDate(processedAt, record, zone = LEDGER_TIMEZONE) {
        if (processedAt) return this.renderTimestamp(processedAt, LEDGER_TABLE_PLACEHOLDER, zone);

        return record?.processed_status === LEDGER_PROCESSED_STATUS.PENDING
            ? LEDGER_PENDING_LABEL
            : LEDGER_TABLE_PLACEHOLDER;
    }

    /**
     * @param {string|null} last4
     * @param {object} record
     */
    renderDestinationAccount(last4, record) {
        // Explicitly unknown, never a blank: money always went somewhere.
        if (!last4) return LEDGER_DESTINATION_UNKNOWN_LABEL;

        // `current_dda` means we could not find the account chosen at the time and
        // fell back to the member's current one — which, if they switched banks,
        // is not where this money went. Say so instead of implying history.
        const isHistorical = record?.destination_account_source === DESTINATION_SOURCE.TRANSFER;
        const name = record?.destination_account_name;
        const qualifier = `Member's current direct deposit account${name ? ` (${name})` : ''} — not necessarily the one used for this transaction`;

        return (
            <span
                className={isHistorical ? styles.account : styles.accountFallback}
                title={isHistorical ? name || 'Account used for this transaction' : qualifier}
            >
                •••• {last4}
                {isHistorical ? null : (
                    <>
                        {/*
                         * The qualifier has to survive without the tooltip: a
                         * `title` does not exist on touch and is not reachable
                         * from a screen reader, so the asterisk alone would tell
                         * a sighted mouse user this is a fallback account and
                         * tell nobody else. The visible marker stays decorative
                         * and the same words go into the accessible name.
                         */}
                        <span aria-hidden className={styles.accountFlag}>
                            *
                        </span>
                        <span className={styles.srOnly}>{qualifier}</span>
                    </>
                )}
            </span>
        );
    }

    /**
     * The Type cell: the type tag, plus the reconciliation marker when the
     * settlement was booked by an admin. Words, not only tone — the amount is
     * also kept neutral, but this is what tells the row apart with colour ignored.
     *
     * @param {string} type
     * @param {object} record
     */
    renderType(type, record) {
        const tag = <TransactionTypeTag type={type} />;
        if (!isReconciliationRow(record)) return tag;
        return (
            <span className={styles.typeCell}>
                {tag}
                <span className={styles.reconciliationTag} title={LEDGER_RECONCILIATION_TAG_TITLE}>
                    {LEDGER_RECONCILIATION_TAG_LABEL}
                </span>
            </span>
        );
    }

    /**
     * An amount, coloured by what it means rather than by its sign — colouring
     * by sign is what painted every advance green, since advances arrive
     * positive. `SignedAmount` is the one rendering of a toned amount, here and
     * on the support ticket.
     *
     * @param {number|string|null} amount
     * @param {object} record
     */
    renderAmount(amount, record) {
        return <SignedAmount amount={amount} tone={this.toneForLedgerRow(record)} />;
    }

    /**
     * The fee on a row, coloured by the same rule as that row's amount.
     *
     * On most rows a fee is a descriptive figure — not a balance owed or
     * settled in its own right — and `toneForLedgerRow` already returns neutral
     * for them, so nothing changes.
     *
     * A settlement is the exception, and the reason this shares the amount's
     * tone instead of hardcoding neutral. Its fee is not a charge: it is the
     * fee half of money already settled, which the service subtracts out of
     * `amount` so the two halves add back up to the row. One settled figure
     * shown in two colours would read as two different kinds of money.
     *
     * A placeholder stays neutral. `—` means the fee is unknown — an instant
     * advance with no linked fee row, or a settlement the service could not
     * split — and green would be colouring a value that is not there.
     * `SignedAmount` applies that rule itself.
     *
     * @param {number|string|null} fee
     * @param {object} record
     */
    renderFee(fee, record) {
        const resolved = resolveFee(fee);
        const tone = resolved === null ? MoneyTones.NEUTRAL : this.toneForLedgerRow(record);
        return <SignedAmount amount={resolved} tone={tone} />;
    }

    /**
     * The User column (Reports tab only): avatar with name and email. Clicking
     * it jumps to the user when the viewer may.
     *
     * @param {object} record
     * @param {{onViewUser?: Function, canViewUser?: boolean}} options
     */
    renderUser(record, { onViewUser, canViewUser = true } = {}) {
        const name = record?.user_name || LEDGER_USER_UNKNOWN_LABEL;
        const clientId = record?.client_id || null;
        const clickable = !!(canViewUser && clientId && onViewUser);
        // With `onClick` the design system renders the whole cell as a button;
        // the label says what pressing it does, not just who the row is about.
        return (
            <Avatar
                name={name}
                subtitle={record?.user_email || undefined}
                size={AvatarSizes.SMALL}
                shape={AvatarShapes.ROUNDED}
                onClick={clickable ? () => onViewUser(clientId) : undefined}
                aria-label={clickable ? `Open user ${name}` : undefined}
                style={clickable ? undefined : { cursor: 'default' }}
            />
        );
    }

    /**
     * The per-row action button. What it does is the one thing the two
     * consumers configure differently.
     *
     * @param {object} record
     * @param {{rowAction: string, onRowAction?: Function}} options
     */
    renderRowAction(record, { rowAction, onRowAction }) {
        const labels = LEDGER_ROW_ACTION_LABELS[rowAction] || LEDGER_ROW_ACTION_LABELS[LEDGER_ROW_ACTION.OPEN_TICKET];
        const Icon = rowAction === LEDGER_ROW_ACTION.VIEW_USER ? UserIcon : TicketIcon;
        return (
            <button
                type="button"
                className={styles.actionBtn}
                onClick={() => onRowAction?.(record)}
                aria-label={labels.ariaLabel}
            >
                <Icon width={16} height={16} />
            </button>
        );
    }

    /**
     * Column definitions for the ledger table.
     *
     * @param {object} options
     * @param {{sortBy: string, sortDir: string}|null} [options.sort]
     * @param {string|null} [options.typeFilter] comma-separated ledger types
     * @param {boolean} [options.showTypeFilter=true] offer the Type column's own filter dropdown
     * @param {boolean} [options.showFees] offer service-fee rows in that dropdown
     * @param {boolean} [options.showUserColumn] prepend the User column (Reports tab)
     * @param {(clientId: string) => void} [options.onViewUser]
     * @param {boolean} [options.canViewUser]
     * @param {boolean} [options.canAction] append the actions column
     * @param {string} [options.rowAction] a LEDGER_ROW_ACTION value
     * @param {(record: object) => void} [options.onRowAction]
     * @param {string} [options.timezone] IANA zone to render timestamps in; named in the headings
     * @returns {object[]}
     */
    getLedgerColumns({
        sort,
        typeFilter,
        showTypeFilter = true,
        showFees = false,
        showUserColumn = false,
        onViewUser,
        canViewUser = true,
        canAction,
        rowAction = LEDGER_ROW_ACTION.OPEN_TICKET,
        onRowAction,
        timezone = LEDGER_TIMEZONE,
    }) {
        const selectedTypes = typeFilter ? String(typeFilter).split(',') : null;
        const zoneLabel = formatLedgerTimezoneLabel(timezone);

        return [
            ...(showUserColumn
                ? [
                      {
                          title: LEDGER_COLUMN_TITLES.USER,
                          dataIndex: 'user_name',
                          key: 'user_name',
                          render: (_, record) => this.renderUser(record, { onViewUser, canViewUser }),
                      },
                  ]
                : []),
            {
                title: LEDGER_COLUMN_TITLES.TYPE,
                dataIndex: 'type',
                key: 'type',
                // The Reports tab hands `type` to its filter bar instead, so
                // the dropdown is optional; the sort stays either way.
                ...(showTypeFilter
                    ? {
                          filters: getLedgerTypeFilterOptions(showFees),
                          filterMultiple: false,
                          filteredValue: selectedTypes,
                      }
                    : {}),
                // Sorts on the stored enum, which happens to order the way the
                // labels read: advance < service_fee < settle.
                sorter: true,
                sortOrder: getLedgerSortOrder('type', sort),
                render: (type, record) => this.renderType(type, record),
            },
            {
                title: LEDGER_COLUMN_TITLES.ADVANCE_TYPE,
                dataIndex: 'transfer_type',
                key: 'transfer_type',
                render: (transferType) => this.renderAdvanceType(transferType),
            },
            {
                title: LEDGER_COLUMN_TITLES.AMOUNT,
                dataIndex: 'amount',
                key: 'amount',
                align: 'right',
                // `sorter: true` = server-side. A comparator here would re-order
                // the current page only, contradicting the rows the server chose.
                sorter: true,
                sortOrder: getLedgerSortOrder('amount', sort),
                render: (amount, record) => this.renderAmount(amount, record),
            },
            {
                title: LEDGER_COLUMN_TITLES.FEE,
                dataIndex: 'fee',
                key: 'fee',
                align: 'right',
                render: (fee, record) => this.renderFee(fee, record),
            },
            {
                title: `${LEDGER_COLUMN_TITLES.CREATED} (${zoneLabel})`,
                dataIndex: 'created_at',
                key: 'created_at',
                sorter: true,
                sortOrder: getLedgerSortOrder('created_at', sort),
                render: (value) => this.renderTimestamp(value, LEDGER_TABLE_PLACEHOLDER, timezone),
            },
            {
                title: `${LEDGER_COLUMN_TITLES.PROCESSED} (${zoneLabel})`,
                dataIndex: 'processed_at',
                key: 'processed_at',
                sorter: true,
                sortOrder: getLedgerSortOrder('processed_at', sort),
                render: (value, record) => this.renderProcessedDate(value, record, timezone),
            },
            {
                title: LEDGER_COLUMN_TITLES.DESTINATION,
                dataIndex: 'destination_account_last4',
                key: 'destination_account_last4',
                render: (last4, record) => this.renderDestinationAccount(last4, record),
            },
            ...(canAction
                ? [
                      {
                          title: (
                              LEDGER_ROW_ACTION_LABELS[rowAction] ||
                              LEDGER_ROW_ACTION_LABELS[LEDGER_ROW_ACTION.OPEN_TICKET]
                          ).title,
                          dataIndex: 'actions',
                          key: 'actions',
                          align: 'right',
                          render: (_, record) => this.renderRowAction(record, { rowAction, onRowAction }),
                      },
                  ]
                : []),
        ];
    }
}

export default LedgerTableController;
